import { Dropdown, Option } from '@fluentui/react-components';
import { Button } from '../../../Button';
import { Flex } from '../../../Flex';
import { Loader } from '../../../Loader';
import { Text } from '../../../Text';
import { Day, TimePattern, TimeSlot } from '@mnspro-cloud/services';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveDialog } from '../../../ResponsiveDialog/ResponsiveDialog';

import styles from './ClassCourseTimeForm.module.css';

export interface ClassCourseTimeFormProps {
  isLoading?: boolean;
  timepattern: TimePattern;
  open: boolean;
  onAdd(weekDayTimeSlot: WeekDayTimeSlot): void;
  onCancel(): void;
}

export interface WeekDayTimeSlot {
  weekDay: Day;
  timeSlot: TimeSlot;
}

interface WeekdayItem {
  header: string;
  item: Day;
}

interface SlotItem {
  header: string;
  item: TimeSlot;
}

export const ClassCourseTimeForm: React.FC<ClassCourseTimeFormProps> = ({
  isLoading,
  timepattern,
  open,
  onAdd,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [weekDay, setWeekDay] = useState<Day>();
  const [slot, setSlot] = useState<TimeSlot>();

  const canSave = useMemo(() => weekDay && slot, [weekDay, slot]);

  return (
    <ResponsiveDialog
      open={open}
      header={t('settings:classbook:courses:title_select_occurrence', {
        defaultValue: 'Stunde auswählen',
      })}
      primaryAction={
        <Button
          appearance="primary"
          disabled={!canSave}
          onClick={() => {
            if (!weekDay || !slot) {
              return;
            }
            onAdd({ weekDay, timeSlot: slot });
          }}
          data-testid="occurrenceAddButton"
        >
          {t('add', { defaultValue: 'Hinzufügen' })}
        </Button>
      }
      secondaryAction={
        <Button
          onClick={() => onCancel()}
          data-testid="occurrenceCancelButton"
        >
          {t('cancel', { defaultValue: 'Abbrechen' })}
        </Button>
      }
    >
      <div className={styles.timeForm}>
        {isLoading && (
          <Loader label="" />
        )}
        {!isLoading && (
          <Flex direction="column">
            <Text className={styles.label}>
              {t('weekday', { defaultValue: 'Wochentag' })}
            </Text>
            <Dropdown
              appearance="filled-darker"
              className={styles.dropdown}
              placeholder={t('select_placeholder', { defaultValue: 'Auswählen...' })}
              value={weekDay ? t(weekDay.toString().toLocaleLowerCase()) : ''}
              selectedOptions={weekDay ? [weekDay] : []}
              onOptionSelect={(_, data) => {
                setWeekDay(data.optionValue as Day);
              }}
            >
              {timepattern.weekdays.map((w) => (
                <Option key={w} value={w} text={t(w.toString().toLowerCase())}>
                  {t(w.toString().toLowerCase())}
                </Option>
              ))}
            </Dropdown>
            <Text className={styles.label}>
              {t('hour', { defaultValue: 'Stunde' })}
            </Text>
            <Dropdown
              appearance="filled-darker"
              className={styles.dropdown}
              placeholder={t('select_placeholder', { defaultValue: 'Auswählen...' })}
              value={slot ? slot.name : ''}
              selectedOptions={slot ? [slot.name] : []}
              onOptionSelect={(_, data) => {
                const selected = timepattern.slots.find((s) => s.name === data.optionValue);
                setSlot(selected);
              }}
            >
              {timepattern.slots.map((s) => (
                <Option key={s.name} value={s.name} text={s.name}>
                  {s.name}
                </Option>
              ))}
            </Dropdown>
          </Flex>
        )}
      </div>
    </ResponsiveDialog>
  );
};
